import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Image as ImageIcon, X, Check, Trash2, Camera, MessageSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS_OPTS = ["Not started", "Design", "Manufacturing", "Installation", "Snagging", "Handed over"];
const STATUS_COLOR = {
  "Not started": "bg-[#F8C21C] text-[#143D63]",
  "Design": "bg-[#7c3aed] text-white",
  "Manufacturing": "bg-[#2563eb] text-white",
  "Installation": "bg-[#0891b2] text-white",
  "Snagging": "bg-orange-500 text-white",
  "Handed over": "bg-green-700 text-white",
};

async function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ProjectsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null); // full project doc opened in drawer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline text-[#8B0000] mb-2">Client Project Dashboards</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#143D63]">Projects</h1>
          <p className="text-[#143D63]/70 text-sm mt-2">Manage active builds. Add milestones, post daily photos — the customer sees them live inside their Studio.</p>
        </div>
        <button
          data-testid="new-project-btn"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-5 py-2.5 text-sm font-bold hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors"
        >
          <Plus size={15} /> New project
        </button>
      </div>

      {loading ? (
        <div className="py-24 text-center"><Loader2 className="mx-auto animate-spin text-[#F8C21C]" /></div>
      ) : items.length === 0 ? (
        <div className="border border-[#143D63]/15 bg-white p-16 text-center">
          <ImageIcon className="text-[#F8C21C] mx-auto mb-4" size={38} />
          <p className="font-serif text-2xl text-[#143D63] mb-1">No projects yet.</p>
          <p className="text-[#143D63]/60 text-sm">Create one — the customer will see it inside their Studio dashboard.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {items.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              data-testid={`project-card-${p.id}`}
              className="text-left border border-[#143D63]/15 bg-white p-5 hover:border-[#143D63]/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="font-serif text-lg text-[#143D63] leading-tight">{p.title}</p>
                <span className={`text-[10px] font-bold px-2 py-1 shrink-0 ${STATUS_COLOR[p.status] || "bg-gray-200"}`}>{p.status}</span>
              </div>
              <p className="text-xs text-[#143D63]/60 truncate mb-4">{p.customer_email}</p>
              <div className="h-2 bg-[#143D63]/10">
                <div className="h-full bg-[#F8C21C]" style={{ width: `${p.progress_pct || 0}%` }} />
              </div>
              <div className="flex justify-between mt-2 text-[11px] text-[#143D63]/60">
                <span>{p.progress_pct || 0}% complete</span>
                <span>{(p.updates || []).length} updates</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={(p) => { setItems((i) => [p, ...i]); setShowNew(false); setSelected(p); }} />}
      {selected && (
        <ProjectDrawer
          project={selected}
          onClose={() => setSelected(null)}
          onSaved={(p) => { setItems((all) => all.map((x) => (x.id === p.id ? p : x))); setSelected(p); }}
        />
      )}
    </>
  );
}

/* ---------- New project modal ---------- */
function NewProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ customer_email: "", title: "", address: "", status: "Design", progress_pct: 0 });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Project created");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#0A0A0A]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="new-project-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#F9F8F6] w-full max-w-md p-8">
        <p className="overline text-[#8B0000] mb-2">Create project</p>
        <h3 className="font-serif text-2xl text-[#143D63] mb-6">New client project</h3>
        <div className="space-y-3">
          <input required type="email" data-testid="np-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="Customer email (must match Studio signup)" className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" />
          <input required data-testid="np-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Project title (e.g. Roy Family 3BHK — Salt Lake)" className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" />
          <input data-testid="np-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Site address (optional)" className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" />
          <select data-testid="np-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none">
            {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <button data-testid="np-submit" disabled={saving} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#143D63] text-[#F9F8F6] px-6 py-3.5 text-sm font-bold hover:bg-[#F8C21C] hover:text-[#143D63] transition-colors">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create project
        </button>
      </form>
    </div>
  );
}

/* ---------- Project detail drawer ---------- */
function ProjectDrawer({ project, onClose, onSaved }) {
  const [p, setP] = useState(project);
  const [saving, setSaving] = useState(false);
  const [newUpdate, setNewUpdate] = useState({ text: "", image: null });
  const [posting, setPosting] = useState(false);

  useEffect(() => setP(project), [project]);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/projects/${p.id}`, payload);
      setP(data);
      onSaved(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onProgress = (val) => setP({ ...p, progress_pct: Number(val) });
  const savedProgress = () => patch({ progress_pct: p.progress_pct });

  const toggleMilestone = async (idx) => {
    const ms = [...(p.milestones || [])];
    ms[idx] = { ...ms[idx], done: !ms[idx].done };
    await patch({ milestones: ms });
  };
  const addMilestone = async () => {
    const name = prompt("Milestone name");
    if (!name) return;
    const ms = [...(p.milestones || []), { name: name.trim(), done: false }];
    await patch({ milestones: ms });
  };
  const removeMilestone = async (idx) => {
    const ms = [...(p.milestones || [])];
    ms.splice(idx, 1);
    await patch({ milestones: ms });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setNewUpdate({ ...newUpdate, image: await fileToDataURL(f) });
    e.target.value = "";
  };
  const postUpdate = async () => {
    if (!newUpdate.text.trim() && !newUpdate.image) {
      toast.error("Add a note or a photo");
      return;
    }
    setPosting(true);
    try {
      const { data } = await api.post(`/projects/${p.id}/update`, newUpdate);
      setP(data);
      onSaved(data);
      setNewUpdate({ text: "", image: null });
      toast.success("Update posted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="project-drawer">
      <div className="flex-1 bg-[#0A0A0A]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#F9F8F6] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#143D63] text-[#F9F8F6] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="overline text-[#F8C21C] text-[10px]">Project</p>
            <p className="font-serif text-xl">{p.title}</p>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-[#F9F8F6] hover:text-[#F8C21C]"><X size={22} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="border border-[#143D63]/15 bg-white p-5">
            <p className="overline text-[#143D63]/60 mb-2">Customer</p>
            <p className="text-sm text-[#143D63]">{p.customer_email}</p>
            {p.address && <p className="text-xs text-[#143D63]/60 mt-1">{p.address}</p>}
          </div>

          <div className="border border-[#143D63]/15 bg-white p-5">
            <p className="overline text-[#143D63]/60 mb-3">Status &amp; Progress</p>
            <select
              data-testid="drawer-status"
              value={p.status}
              onChange={(e) => patch({ status: e.target.value })}
              className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none mb-4"
            >
              {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
            </select>
            <div className="flex items-center gap-3">
              <input
                data-testid="drawer-progress"
                type="range" min={0} max={100} step={5}
                value={p.progress_pct || 0}
                onChange={(e) => onProgress(e.target.value)}
                onMouseUp={savedProgress} onTouchEnd={savedProgress}
                className="flex-1 accent-[#F8C21C]"
              />
              <span className="font-bold text-[#8B0000] w-12 text-right">{p.progress_pct || 0}%</span>
            </div>
          </div>

          <div className="border border-[#143D63]/15 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-[#143D63]/60">Milestones</p>
              <button onClick={addMilestone} className="text-xs text-[#8B0000] font-bold inline-flex items-center gap-1" data-testid="add-milestone-btn"><Plus size={12} /> Add</button>
            </div>
            <ul className="space-y-2">
              {(p.milestones || []).map((m, i) => (
                <li key={i} className="flex items-center gap-3 group">
                  <button
                    onClick={() => toggleMilestone(i)}
                    data-testid={`milestone-toggle-${i}`}
                    className={`w-5 h-5 border flex items-center justify-center shrink-0 ${m.done ? "bg-green-700 border-green-700 text-white" : "border-[#143D63]/30 bg-white"}`}
                  >
                    {m.done && <Check size={12} />}
                  </button>
                  <span className={`text-sm flex-1 ${m.done ? "text-[#143D63]/50 line-through" : "text-[#143D63]"}`}>{m.name}</span>
                  <button onClick={() => removeMilestone(i)} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#8B0000]"><Trash2 size={13} /></button>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-[#143D63]/15 bg-white p-5">
            <p className="overline text-[#143D63]/60 mb-3">Post daily update</p>
            <textarea
              data-testid="update-text"
              value={newUpdate.text}
              onChange={(e) => setNewUpdate({ ...newUpdate, text: e.target.value })}
              rows={3}
              placeholder="e.g. Kitchen carcasses installed today. Countertop template scheduled tomorrow."
              className="w-full border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none resize-none"
            />
            {newUpdate.image && (
              <div className="mt-3 relative">
                <img src={newUpdate.image} alt="update" className="w-full max-h-48 object-cover border border-[#143D63]/15" />
                <button onClick={() => setNewUpdate({ ...newUpdate, image: null })} className="absolute top-2 right-2 bg-[#8B0000] text-white p-1"><X size={12} /></button>
              </div>
            )}
            <div className="mt-3 flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-[#143D63] cursor-pointer border border-[#143D63]/25 px-3 py-2 hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors" data-testid="update-photo-label">
                <Camera size={13} /> Attach photo
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="update-photo" />
              </label>
              <button
                onClick={postUpdate}
                disabled={posting}
                data-testid="update-post-btn"
                className="inline-flex items-center gap-2 bg-[#8B0000] text-[#F9F8F6] px-4 py-2 text-xs font-bold hover:bg-[#143D63] transition-colors disabled:opacity-60"
              >
                {posting ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />} Post update
              </button>
            </div>
          </div>

          <div className="border border-[#143D63]/15 bg-white p-5">
            <p className="overline text-[#143D63]/60 mb-3">Timeline ({(p.updates || []).length})</p>
            {(p.updates || []).length === 0 ? (
              <p className="text-sm text-[#143D63]/50">No updates yet. Post the first one above.</p>
            ) : (
              <ul className="space-y-4">
                {(p.updates || []).map((u, i) => (
                  <li key={i} className="border-l-2 border-[#F8C21C] pl-4">
                    <p className="text-xs text-[#143D63]/60 mb-1">{new Date(u.at).toLocaleString("en-IN")}</p>
                    {u.image && <img src={u.image} alt="" loading="lazy" className="w-full max-h-56 object-cover border border-[#143D63]/15 mb-2" />}
                    {u.text && <p className="text-sm text-[#143D63] leading-relaxed whitespace-pre-line">{u.text}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-[11px] text-[#143D63]/40 text-center">{saving ? "Saving…" : "All changes autosave."}</p>
        </div>
      </div>
    </div>
  );
}
